import React from 'react';
import { Card, Badge, DataTable } from '@/components/ui';
import { Target, Users, Clock } from 'lucide-react';
import type { Column } from '@/types';
import { formatTeamWeeks, formatPercent } from '@/utils/formatters';

interface Epic {
  id: string;
  name: string;
  status: string;
  role: 'primary' | 'contributing';
  requestedTeamWeeks: number;
  allocatedTeamWeeks: number;
  utilizationPercentage: number;
  investibleStructure: {
    name: string;
    category: string;
  };
}

interface EpicAccountability {
  primaryEpics: Epic[];
  contributingEpics: Epic[];
  totalPrimaryCapacity: number;
  totalContributingCapacity: number;
}

interface EpicAccountabilityViewProps {
  epicAccountability: EpicAccountability | null;
  isLoading: boolean;
}

const EpicAccountabilityView: React.FC<EpicAccountabilityViewProps> = ({
  epicAccountability,
  isLoading,
}) => {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      DRAFT: { variant: 'secondary' as const, label: 'Draft' },
      SUBMITTED: { variant: 'info' as const, label: 'Submitted' },
      PRIORITIZED: { variant: 'warning' as const, label: 'Prioritized' },
      COMMITTED: { variant: 'success' as const, label: 'Committed' },
      STRETCH: { variant: 'warning' as const, label: 'Stretch' },
      DELIVERED: { variant: 'success' as const, label: 'Delivered' },
      CANCELLED: { variant: 'danger' as const, label: 'Cancelled' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getRoleBadge = (role: string) => {
    return role === 'primary' ? (
      <Badge variant="primary">Primary</Badge>
    ) : (
      <Badge variant="secondary">Contributing</Badge>
    );
  };

  const getCategoryBadge = (category: string) => {
    const categoryConfig = {
      BUSINESS: { variant: 'primary' as const, label: 'Business' },
      TECH: { variant: 'info' as const, label: 'Tech' },
      SUPPORT: { variant: 'secondary' as const, label: 'Support' },
    };
    
    const config = categoryConfig[category as keyof typeof categoryConfig] || categoryConfig.BUSINESS;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const epicColumns: Column<Epic>[] = [
    {
      key: 'role',
      header: 'Role',
      render: (value) => getRoleBadge(value),
    },
    {
      key: 'name',
      header: 'EPIC Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground">{value}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {row.investibleStructure.name}
          </div>
        </div>
      ),
    },
    {
      key: 'investibleStructure',
      header: 'Category',
      render: (value) => getCategoryBadge(value.category),
    },
    {
      key: 'status',
      header: 'Status',
      filterable: true,
      render: (value) => getStatusBadge(value),
    },
    {
      key: 'capacity',
      header: 'Capacity (TW)',
      render: (_, row) => (
        <div className="text-right">
          <div className="text-foreground font-medium">
            {formatTeamWeeks(row.allocatedTeamWeeks)} / {formatTeamWeeks(row.requestedTeamWeeks)}
          </div>
          <div className="text-sm text-muted-foreground">
            {formatPercent(row.utilizationPercentage)} utilized
          </div>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Card className="mb-6">
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-muted/50 rounded w-1/2 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-muted/50 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-muted/50 rounded"></div>
        </div>
      </Card>
    );
  }

  if (!epicAccountability) {
    return (
      <Card className="mb-6">
        <div className="p-6 text-center text-muted-foreground">
          No EPIC accountability data available for this program
        </div>
      </Card>
    );
  }

  const allEpics = [...epicAccountability.primaryEpics, ...epicAccountability.contributingEpics];
  const totalCapacity = epicAccountability.totalPrimaryCapacity + epicAccountability.totalContributingCapacity;

  return (
    <Card className="mb-6">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">EPIC Accountability</h3>
            <p className="text-sm text-muted-foreground">
              Primary and contributing EPICs for this program
            </p>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-foreground">
              {formatTeamWeeks(totalCapacity)}
            </div>
            <div className="text-sm text-muted-foreground">Total Capacity</div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Target className="w-5 h-5 text-accent-blue" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Primary EPICs</h4>
                <p className="text-xs text-muted-foreground">Full accountability</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="text-foreground font-medium">{epicAccountability.primaryEpics.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Capacity:</span>
                <span className="text-foreground font-medium">
                  {formatTeamWeeks(epicAccountability.totalPrimaryCapacity)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Committed:</span>
                <span className="text-status-success font-medium">
                  {epicAccountability.primaryEpics.filter(e => e.status === 'COMMITTED').length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Contributing EPICs</h4>
                <p className="text-xs text-muted-foreground">Shared accountability</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="text-foreground font-medium">{epicAccountability.contributingEpics.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Capacity:</span>
                <span className="text-foreground font-medium">
                  {formatTeamWeeks(epicAccountability.totalContributingCapacity)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Committed:</span>
                <span className="text-status-success font-medium">
                  {epicAccountability.contributingEpics.filter(e => e.status === 'COMMITTED').length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-status-success/10 rounded-lg">
                <Clock className="w-5 h-5 text-status-success" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Total Portfolio</h4>
                <p className="text-xs text-muted-foreground">Combined view</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total EPICs:</span>
                <span className="text-foreground font-medium">{allEpics.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Capacity:</span>
                <span className="text-foreground font-medium">
                  {formatTeamWeeks(totalCapacity)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Avg Utilization:</span>
                <span className="text-foreground font-medium">
                  {formatPercent(allEpics.length > 0 ?
                    (allEpics.reduce((sum, e) => sum + e.utilizationPercentage, 0) / allEpics.length) : 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* EPICs Table */}
        <div>
          <h4 className="font-medium text-foreground mb-4">All EPICs</h4>
          <DataTable
            data={allEpics}
            columns={epicColumns}
            loading={isLoading}
            searchPlaceholder="Search EPICs..."
            emptyMessage="No EPICs found for this program."
            pageSize={10}
          />
        </div>

        {/* Accountability Insights */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="font-medium text-foreground mb-4">Accountability Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <h5 className="text-sm font-medium text-foreground mb-2">Primary Accountability</h5>
              <p className="text-xs text-muted-foreground">
                You have full delivery responsibility for {epicAccountability.primaryEpics.length} EPICs 
                with {formatTeamWeeks(epicAccountability.totalPrimaryCapacity)} of capacity.
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <h5 className="text-sm font-medium text-foreground mb-2">Cross-Program Coordination</h5>
              <p className="text-xs text-muted-foreground">
                You contribute to {epicAccountability.contributingEpics.length} EPICs owned by other programs, 
                requiring coordination and communication.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EpicAccountabilityView;